import React, { useState } from 'react';
import { FormMetadata } from '../../types';
import { getStoredUuidPathSegment } from '../../utils/urlUtils';
import { validateMandatoryFormData } from '../../utils/formValidation';
import { useLanguage } from '../../LanguageContext';
import { getTranslation } from '../../consts/translations';
import { updateFormData } from '../../api/AuthorizationApi';
import { getValidationMessage } from '../../utils/validationMessages';

interface FormSubmitButtonProps {
  isLoading: boolean;
  errors: Record<string, string>;
  setErrors: (value: Record<string, string>) => void;
  formData: FormMetadata;
  setIsLoading: (value: boolean) => void;
  setShowFinishedFormPage: (value: boolean) => void;
  disabled: boolean;
  logout: () => void;
  showPreview: boolean;
  setShowPreview: (value: boolean) => void;
  previewContent: React.ReactNode;
}

const FormSubmitButton: React.FC<FormSubmitButtonProps> = ({
  isLoading,
  errors,
  setErrors,
  formData,
  setIsLoading,
  setShowFinishedFormPage,
  disabled,
  logout,
  showPreview,
  setShowPreview,
  previewContent,
}) => {
  const { language } = useLanguage();
  const translation = getTranslation(language);
  const [formErrors, setFormErrors] = useState<string>('');

  const validateForm = () => {
    const validationErrors = validateMandatoryFormData(formData, language);
    const newErrors = { ...errors, ...validationErrors };
    setErrors(newErrors);
    return newErrors;
  };

  const handleSubmit = async () => {
    setIsLoading(true);

    const errors = validateForm();
    if (Object.keys(errors).length > 0) {
      setIsLoading(false);
      setShowPreview(false);
      const firstErrorKey = Object.keys(errors)[0];
      const el = document.getElementById(firstErrorKey);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        el.focus?.();
      }
      return;
    }

    const uuid = getStoredUuidPathSegment();

    try {
      await updateFormData(formData, translation, logout, uuid, true);
      setShowFinishedFormPage(true);
    } catch (error: unknown) {
      if (
        typeof error === 'object' &&
        error !== null &&
        'errorQuestionKey' in error &&
        typeof (error as { errorQuestionKey: unknown }).errorQuestionKey === 'string'
      ) {
        const errorKey = (error as { errorQuestionKey: string }).errorQuestionKey;
        const el = document.getElementById(errorKey);

        setErrors({
          ...errors,
          [errorKey]: getValidationMessage(language, 'error_question'),
        });

        if (el) {
          el.scrollIntoView({ behavior: 'smooth', block: 'center' });
          el.focus?.();
        }
      } else {
        setFormErrors(getValidationMessage(language, 'general_submit_error'));
      }
      setShowPreview(false);
    } finally {
      setIsLoading(false);
    }
  };

  if (showPreview) {
    return (
      <div className='preview-modal-overlay'>
        <div className='preview-modal'>
          <button
            className='preview-modal-close'
            onClick={() => setShowPreview(false)}
            aria-label='Close preview'
          >
            ×
          </button>
          <h2 className='preview-modal-title'>{translation.preview_modal_title}</h2>
          <div className='preview-modal-body'>{previewContent}</div>
          <div className='preview-modal-footer'>
            <button className='preview-btn cancel' onClick={() => setShowPreview(false)}>
              {translation.close_label}
            </button>
            <button
              className={`submit-btn ${isLoading ? 'disabled-btn' : ''}`}
              disabled={disabled || isLoading}
              onClick={() => {
                handleSubmit();
              }}
            >
              {isLoading ? <div className='spinner'></div> : translation.finish_label}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className='submit-wrapper'>
      <button
        type='submit'
        className={`submit-btn ${isLoading ? 'disabled-btn' : ''}`}
        disabled={disabled || isLoading}
        onClick={() => {
          setShowPreview(true);
        }}
      >
        {isLoading ? <div className='spinner'></div> : translation.finish_label}
      </button>
      {formErrors && <div className='error-message'>{formErrors}</div>}
    </div>
  );
};

export default FormSubmitButton;
